import React, { useState, useEffect } from 'react';
import { Button, Space, Popover, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import styles from './DrawingToolbox.module.scss';
import FreePenIcon from '../FreePenIcon';
import type { DrawingTool } from '../VideoPlayer/VideoPlayer';

const toolbarAsset = (filename: string) => `/toolbar/${encodeURIComponent(filename)}`;

const TB = {
  select: toolbarAsset('ToolBar Select Icon.svg'),
  draw: toolbarAsset('ToolBar Draw Icon.svg'),
  shape: toolbarAsset('ToolBar Shape Icon.svg'),
  shape2: toolbarAsset('ToolBar Shape 2 Icon.svg'),
  arrow: toolbarAsset('ToolBar Arrow Icon.svg'),
  text: toolbarAsset('ToolBar Text Icon.svg'),
  undo: toolbarAsset('ToolBar Undo Icon.svg'),
  redo: toolbarAsset('ToolBar Redo Icon.svg'),
  clear: toolbarAsset('ToolBar Clear Icon.svg'),
} as const;

const ToolbarImg: React.FC<{ src: string; alt?: string }> = ({ src, alt = '' }) => (
  <img src={src} alt={alt} className={styles.toolbarIcon} />
);

const cxToolBtn = (active: boolean) =>
  `${styles.toolBtn}${active ? ` ${styles.toolBtnSelected}` : ''}`;

/** Tooltip / aria: kısa araç adları */
const TOOLBAR_HINT = {
  select: 'Select',
  selectDisabled: 'Select (add a drawing first)',
  freeDraw: 'Free Draw',
  circle: 'Circle',
  square: 'Square',
  arrow: 'Arrow',
  text: 'Text',
  color: 'Color',
  undo: 'Undo',
  redo: 'Redo',
  deleteShape: 'Delete',
  clearCanvas: 'Clear',
  openToolbar: 'Drawing tools',
} as const;

/** Ant `Button` üzerinde native `title` çoğu ortamda görünmüyor; Tooltip kullan */
const TOOLBAR_TT_Z = 10_050;

/** Popover zaten Tooltip kullanır; palet paneli seek bar / kontrollerin üstünde kalsın */
const PALETTE_POPOVER_Z = TOOLBAR_TT_Z + 20;

const ToolbarTooltip: React.FC<{
  label: string;
  /** `disabled` düğmede hover için (Ant önerisi: span sarıcı) */
  disabledHover?: boolean;
  children: React.ReactElement;
}> = ({ label, disabledHover, children }) => (
  <Tooltip
    title={label}
    placement="top"
    autoAdjustOverflow={false}
    mouseEnterDelay={0.12}
    getPopupContainer={() => document.body}
    styles={{ root: { zIndex: TOOLBAR_TT_Z } }}
  >
    {disabledHover ? (
      <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>{children}</span>
    ) : (
      children
    )}
  </Tooltip>
);

/** Çizimde yalnızca bu dört renk seçilebilir */
export const DRAWING_PALETTE = ['#FF4D4F', '#00C271', '#7EC4C0', '#9B86BC'] as const;

function normalizeHex(hex: string): string {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return `#${h.toUpperCase()}`;
}

interface PaletteColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  title?: string;
  getPopupContainer?: () => HTMLElement;
}

const PaletteColorPicker: React.FC<PaletteColorPickerProps> = ({
  value,
  onChange,
  title,
  getPopupContainer,
}) => {
  const [open, setOpen] = useState(false);
  const vNorm = normalizeHex(value || '#000000');

  const panel = (
    <div
      className={styles.palettePopup}
      onMouseDown={(e) => e.stopPropagation()}
      role="listbox"
      aria-label={title}
    >
      {DRAWING_PALETTE.map((hex) => {
        const isSel = normalizeHex(hex) === vNorm;
        return (
          <button
            key={hex}
            type="button"
            role="option"
            aria-selected={isSel}
            className={styles.paletteSwatch}
            style={{ backgroundColor: hex }}
            title={hex}
            onClick={() => {
              onChange(hex);
              setOpen(false);
            }}
          />
        );
      })}
    </div>
  );

  /* ToolbarTooltip kullanma: Popover içerde zaten Tooltip kullanıyor; iç içe olunca tıklama / açılma bozulabiliyor */
  const trigger = (
    <button
      type="button"
      className={styles.paletteTrigger}
      style={{ backgroundColor: value }}
      aria-label={title ?? 'Color'}
      title={title && title !== '' ? title : undefined}
    />
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={panel}
      trigger="click"
      placement="top"
      arrow={false}
      zIndex={PALETTE_POPOVER_Z}
      overlayClassName={styles.paletteColorPopover}
      getPopupContainer={getPopupContainer ?? (() => document.body)}
      autoAdjustOverflow={false}
    >
      {trigger}
    </Popover>
  );
};

interface CompactDrawingToolboxProps {
  tool: DrawingTool;
  color: string;
  onToolChange: (tool: DrawingTool) => void;
  onColorChange: (color: string) => void;
  onClear: () => void;
  onSave: () => void;
  onDeleteSelected?: () => void;
  onChangeSelectedColor?: (color: string) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  hasSelectedShape?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  selectedShapeColor?: string;
  drawingSessionActive?: boolean;
  onDrawingSessionChange?: (active: boolean) => void;
  /** En az bir şekil yoksa Select aracı kullanılamaz */
  hasShapes?: boolean;
  /** controlBar: alt oynatıcı çubuğu; popover: küçük kalem; docked: dikey şerit (nadir) */
  variant?: 'popover' | 'docked' | 'controlBar';
}

const CompactDrawingToolbox: React.FC<CompactDrawingToolboxProps> = ({
  tool,
  color,
  onToolChange,
  onColorChange,
  onClear,
  onSave: _onSave,
  onDeleteSelected,
  onChangeSelectedColor,
  onUndo,
  onRedo,
  hasSelectedShape = false,
  canUndo = false,
  canRedo = false,
  selectedShapeColor,
  drawingSessionActive = false,
  onDrawingSessionChange,
  hasShapes = true,
  variant = 'popover',
}) => {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const isDocked = variant === 'docked';
  const isControlBar = variant === 'controlBar';
  const selectDisabled = !hasShapes;

  useEffect(() => {
    if (isDocked || isControlBar) return;
    if (!drawingSessionActive) setPaletteOpen(false);
  }, [drawingSessionActive, isDocked, isControlBar]);

  useEffect(() => {
    if (tool === 'select' && !hasShapes) {
      onToolChange('none');
    }
  }, [tool, hasShapes, onToolChange]);

  const paletteDisplayValue = hasSelectedShape ? (selectedShapeColor || color) : color;

  const handlePaletteColorChange = (hex: string) => {
    onColorChange(hex);
    if (hasSelectedShape && onChangeSelectedColor) {
      onChangeSelectedColor(hex);
    }
  };

  const toolRowCompact = (
    <Space.Compact>
      <ToolbarTooltip
        label={selectDisabled ? TOOLBAR_HINT.selectDisabled : TOOLBAR_HINT.select}
        disabledHover={selectDisabled}
      >
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'select')}
          icon={<ToolbarImg src={TB.select} alt="" />}
          onClick={() => onToolChange('select')}
          type="default"
          disabled={selectDisabled}
          aria-label={selectDisabled ? TOOLBAR_HINT.selectDisabled : TOOLBAR_HINT.select}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.freeDraw}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'line')}
          icon={<ToolbarImg src={TB.draw} alt="" />}
          onClick={() => onToolChange('line')}
          type="default"
          aria-label={TOOLBAR_HINT.freeDraw}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.circle}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'circle')}
          icon={<ToolbarImg src={TB.shape} alt="" />}
          onClick={() => onToolChange('circle')}
          type="default"
          aria-label={TOOLBAR_HINT.circle}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.square}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'rect')}
          icon={<ToolbarImg src={TB.shape2} alt="" />}
          onClick={() => onToolChange('rect')}
          type="default"
          aria-label={TOOLBAR_HINT.square}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.arrow}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'arrow')}
          icon={<ToolbarImg src={TB.arrow} alt="" />}
          onClick={() => onToolChange('arrow')}
          type="default"
          aria-label={TOOLBAR_HINT.arrow}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.text}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'text')}
          icon={<ToolbarImg src={TB.text} alt="" />}
          onClick={() => onToolChange('text')}
          type="default"
          aria-label={TOOLBAR_HINT.text}
        />
      </ToolbarTooltip>
    </Space.Compact>
  );

  const toolButtons = isDocked ? (
    <Space direction="vertical" size={14} className={styles.dockedToolStack}>
      <ToolbarTooltip
        label={selectDisabled ? TOOLBAR_HINT.selectDisabled : TOOLBAR_HINT.select}
        disabledHover={selectDisabled}
      >
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'select')}
          icon={<ToolbarImg src={TB.select} alt="" />}
          onClick={() => onToolChange('select')}
          type="default"
          disabled={selectDisabled}
          aria-label={selectDisabled ? TOOLBAR_HINT.selectDisabled : TOOLBAR_HINT.select}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.freeDraw}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'line')}
          icon={<ToolbarImg src={TB.draw} alt="" />}
          onClick={() => onToolChange('line')}
          type="default"
          aria-label={TOOLBAR_HINT.freeDraw}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.circle}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'circle')}
          icon={<ToolbarImg src={TB.shape} alt="" />}
          onClick={() => onToolChange('circle')}
          type="default"
          aria-label={TOOLBAR_HINT.circle}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.square}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'rect')}
          icon={<ToolbarImg src={TB.shape2} alt="" />}
          onClick={() => onToolChange('rect')}
          type="default"
          aria-label={TOOLBAR_HINT.square}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.arrow}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'arrow')}
          icon={<ToolbarImg src={TB.arrow} alt="" />}
          onClick={() => onToolChange('arrow')}
          type="default"
          aria-label={TOOLBAR_HINT.arrow}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label={TOOLBAR_HINT.text}>
        <Button
          style={{ width: 20, height: 20 }}
          className={cxToolBtn(tool === 'text')}
          icon={<ToolbarImg src={TB.text} alt="" />}
          onClick={() => onToolChange('text')}
          type="default"
          aria-label={TOOLBAR_HINT.text}
        />
      </ToolbarTooltip>
    </Space>
  ) : (
    toolRowCompact
  );

  const actionSpace = (
    <div className={styles.actionSpaceRow}>
      <div className={styles.undoRedoCluster}>
        {onUndo && (
          <ToolbarTooltip label={TOOLBAR_HINT.undo} disabledHover={!canUndo}>
            <Button
              style={{ width: 20, height: 20 }}
              icon={<ToolbarImg src={TB.undo} alt="" />}
              onClick={onUndo}
              disabled={!canUndo}
              aria-label={TOOLBAR_HINT.undo}
              className={styles.undoRedoBtn}
            />
          </ToolbarTooltip>
        )}
        {onRedo && (
          <ToolbarTooltip label={TOOLBAR_HINT.redo} disabledHover={!canRedo}>
            <Button
              style={{ width: 20, height: 20 }}
              icon={<ToolbarImg src={TB.redo} alt="" />}
              onClick={onRedo}
              disabled={!canRedo}
              aria-label={TOOLBAR_HINT.redo}
              className={styles.undoRedoBtn}
            />
          </ToolbarTooltip>
        )}
      </div>
      <div
        className={`${styles.actionSpaceTrailing}${isControlBar ? ` ${styles.actionSpaceTrailingWrap}` : ''}`}
      >
        {tool === 'select' && hasSelectedShape && (
          <>
            {onDeleteSelected && (
              <ToolbarTooltip label={TOOLBAR_HINT.deleteShape}>
                <Button
                  style={{ width: 20, height: 20 }}
                  icon={<DeleteOutlined />}
                  danger
                  onClick={onDeleteSelected}
                  aria-label={TOOLBAR_HINT.deleteShape}
                  className={styles.deleteBtn}
                />
              </ToolbarTooltip>
            )}
          </>
        )}
        <ToolbarTooltip label={TOOLBAR_HINT.clearCanvas}>
          <Button
            icon={<ToolbarImg src={TB.clear} alt="" />}
            onClick={onClear}
            aria-label={TOOLBAR_HINT.clearCanvas}
            style={{ width: 20, height: 20 }}
          />
        </ToolbarTooltip>
      </div>
    </div>
  );

  if (isControlBar) {
    return (
      <div
        className={styles.compactToolboxControlBar}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.toolboxContentControlBar}>
          <div className={styles.toolGroup}>{toolRowCompact}</div>
          <div className={styles.toolGroup}>
            <PaletteColorPicker
              value={paletteDisplayValue}
              onChange={handlePaletteColorChange}
              title={TOOLBAR_HINT.color}
              getPopupContainer={() => document.body}
            />
          </div>
          {actionSpace}
        </div>
      </div>
    );
  }

  const toolboxContent = (
    <div
      className={`${styles.compactToolbox} ${isDocked ? styles.compactToolboxDocked : ''}`}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
    >
      <div
        className={`${styles.toolboxContent} ${isDocked ? styles.toolboxContentDocked : ''}`}
      >
        <div className={styles.toolGroup}>{toolButtons}</div>

        <div className={styles.toolGroup}>
          <PaletteColorPicker
            value={paletteDisplayValue}
            onChange={handlePaletteColorChange}
            title={TOOLBAR_HINT.color}
            getPopupContainer={() => document.body}
          />
        </div>

        {actionSpace}
      </div>
    </div>
  );

  if (isDocked) {
    return toolboxContent;
  }

  return (
    <Popover
      content={toolboxContent}
      trigger="click"
      open={paletteOpen}
      onOpenChange={(next) => {
        if (next) {
          setPaletteOpen(true);
          onDrawingSessionChange?.(true);
        } else {
          setPaletteOpen(false);
          onDrawingSessionChange?.(false);
          onToolChange('none');
        }
      }}
      placement="top"
      overlayClassName={styles.compactToolboxPopover}
      destroyTooltipOnHide={false}
      autoAdjustOverflow={false}
      getPopupContainer={() => document.body}
    >
      <ToolbarTooltip label={TOOLBAR_HINT.openToolbar}>
        <Button
          htmlType="button"
          style={{
            width: 14,
            height: 14,
            minHeight: 14,
            maxHeight: 14,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: '#F6FFED',
            padding: 0,
            lineHeight: 1,
          }}
          icon={<FreePenIcon width={14} height={14} fill="#F6FFED" />}
          aria-label={TOOLBAR_HINT.openToolbar}
        />
      </ToolbarTooltip>
    </Popover>
  );
};

export default CompactDrawingToolbox;
